import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  AngebotsId,
  getInfosFromDeltafaktoren,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload
} from '@ps20-frontend/shop-integration-redux';
import {
  addVorbelegteVorschaedenToTaasRequestModel,
  App,
  getTarifbereich,
  Produktlinie
} from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
  TaasData
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, Feature, TaasRequestModel, TaasTkvV2Data } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { TkvActions } from '../tkvActions';
import { buildTaasTkvV2DataForShop, buildTaasTkvV2DataForStrecke } from './transformer/tkvData.transformer';


export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.TKV_V2));
  yield takeLatest(TkvActions.QUOTEDB_REQUESTED, safeSaga(quoteDbInit, App.TKV_V2));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  if (state.transientState.productLoadingIsInProgress) {
    PS20Logger.info(
      'TKV_V2.QuoteDb',
      `cancelled: productLoadingIsInProgress=true angebotsId=${state.angebotsId}`,
      App.TKV_V2,
    );
    yield cancel();
    return;
  }
  let taasRequestModel: TaasRequestModel<TaasTkvV2Data>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;

  const spartenAngebotsId: AngebotsId = {
    angebotsId: state.angebotsId,
    appId: App.TKV_V2
  };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }
  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
    taasRequestModel = addVorbelegteVorschaedenToTaasRequestModel(
      taasRequestModel,
      state.transientState.vorvertragVorschaeden,
      'anzVorschaedenOp'
    );
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasData> = {
    angebotsId: spartenAngebotsId,
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    antragsnummer: state.antragsnummer
  };

  getInfosFromDeltafaktoren(state.taasInfo, state.vertragsoptionen, quoteDbPayload);

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const spartenAngebotsId: AngebotsId = {
    angebotsId: state.angebotsId,
    appId: App.TKV_V2
  };
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: spartenAngebotsId,
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasTkvV2Data> {
  const taasData: TaasTkvV2Data = buildTaasTkvV2DataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen, state.produkt.spartenType, {
      aaWorldTkvFT: state.transientState.featureToggles[Feature.AA_WORLD_TKV]
    }),
    taasData,
    getTarifbereich(state.versicherungsnehmer)
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasTkvV2Data> {

  const tkvData: TaasTkvV2Data = buildTaasTkvV2DataForShop(state);
  const newHashedTaasRequest = createHashedTaasData(tkvData);

  let taasRequestModel: TaasRequestModel<TaasTkvV2Data> = buildTaasRequestModelForShop(
    tkvData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer)
  );
  taasRequestModel = addSpartenspezifischeDatenToTaasRequestModel(taasRequestModel, state);
  return taasRequestModel;
}

function addSpartenspezifischeDatenToTaasRequestModel(
  taasRequestModel: TaasRequestModel<TaasTkvV2Data>,
  state: IAppState
) {
  taasRequestModel.taasSparte.kennungVorvers = state.vertragsoptionen.vorversicherungVorhanden;
  return taasRequestModel;
}
